import { Search, X } from 'lucide-react';
import { type ReactElement, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';

interface SearchBoxProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly inputRef: Ref<HTMLInputElement>;
}

/**
 * Search input row for the HPO browse dialog. Shows the search icon, the
 * input itself, and a clear button when there's text. Form submission /
 * keystroke handling is left to the parent — the box only manages its
 * visible chrome.
 */
export function SearchBox({ value, onChange, inputRef }: SearchBoxProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 h-9 focus-within:ring-2 focus-within:ring-ring">
      <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
      <Input
        ref={inputRef}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        placeholder={t('patient.phenotypes.browse.searchPlaceholder')}
        className="h-7 flex-1 border-0 px-0 text-sm shadow-none focus-visible:ring-0"
        aria-label={t('patient.phenotypes.browse.searchPlaceholder')}
      />
      {value.length > 0 && (
        <button
          type="button"
          onClick={() => {
            onChange('');
          }}
          aria-label={t('vocab.picker.clear')}
          className="shrink-0 rounded-md p-0.5 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
